import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { ContractForm } from '../components/contracts/ContractForm';
import { Button, LoadingSpinner, useToastHelpers } from '../components/ui';
import { useContract, useUpdateContract } from '../hooks/useContracts';
import { markFormSaved } from '../hooks/useUnsavedChanges';
import { apiErrorMessage } from '../lib/api-error-message';
import type { CreateContractInput, UpdateContractInput } from '@armard/shared';

export const Route = createFileRoute('/contracts_/$id_/edit')({ component: EditContractPage });

function EditContractPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToastHelpers();
  const { data: contract, isLoading, error } = useContract(id);
  const updateContract = useUpdateContract();

  const save = async (values: CreateContractInput | UpdateContractInput) => {
    try {
      const updated = await updateContract.mutateAsync({ id, data: values as UpdateContractInput });
      markFormSaved();
      toast.success('Vertrag gespeichert');
      await navigate({ to: '/contracts/$id', params: { id: updated.id } });
    } catch (saveError) {
      toast.error(apiErrorMessage(saveError, 'Fehler beim Speichern des Vertrags'));
    }
  };

  return (
    <ProtectedRoute>
      <AppShell>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error || !contract ? (
          <div className="text-center py-12">
            <p className="text-red-400">Vertrag nicht gefunden</p>
            <Button className="mt-4" onClick={() => navigate({ to: '/contracts' })}>
              Zurück zur Liste
            </Button>
          </div>
        ) : contract.status !== 'draft' && contract.status !== 'paused' ? (
          <div className="max-w-xl mx-auto py-12 text-center space-y-4">
            <h1 className="text-xl text-white">Dieser Vertrag kann nicht bearbeitet werden.</h1>
            <p className="text-white/60">
              Nur Verträge im Entwurf oder mit pausierter Abrechnung lassen sich ändern. Der
              aktuelle Status ist „{contract.status}“.
            </p>
            <Button onClick={() => navigate({ to: '/contracts/$id', params: { id } })}>
              Zum Vertrag
            </Button>
          </div>
        ) : (
          <ContractForm
            key={contract.id}
            initial={contract}
            title="Vertrag bearbeiten"
            submitLabel="Änderungen speichern"
            isSaving={updateContract.isPending}
            onCancel={() => navigate({ to: '/contracts/$id', params: { id } })}
            onSave={save}
          />
        )}
      </AppShell>
    </ProtectedRoute>
  );
}
